import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Text,
  VStack,
  Heading,
  Checkbox,
  useColorModeValue,
  Select,
  FormLabel,
  Textarea,
  HStack,
} from '@chakra-ui/react';
import ReCAPTCHA from 'react-google-recaptcha';
import { EnrolmentData, PreferredCourse } from "../Common/types";


const default_tenant: number = Number(process.env.REACT_APP_DEFAULT_TENANT_ID);
const resolvedTenantId = isNaN(default_tenant) ? -1 : default_tenant;

if (isNaN(default_tenant)) {
  console.error(
    "REACT_APP_DEFAULT_TENANT_ID is invalid or not set. Falling back to default tenant ID (-1)."
  );
}


interface EnrolmentFormProps {
    mode: 'public' | 'create-by-admin' | 'review';
    enrolmentMode?: 'online' | 'offline';
    tenantId?: number;
    tenantName?: string;
    studentEnrolmentData?: EnrolmentData;
    onSave: (data: EnrolmentData) => void;
    formReset: boolean;
}

const EnrolmentForm: React.FC<EnrolmentFormProps> = ({ mode, enrolmentMode = 'offline', tenantId, tenantName, studentEnrolmentData, onSave, formReset }) => {
  const isOnline = enrolmentMode === 'online';

const initialEnrolmentData: EnrolmentData = {
    mother: {
      givenName: '', surname: '', email: '', contactNo: '', primaryContact: true,
    },
    father: isOnline ? null : {
      givenName: '', surname: '', email: '', contactNo: '', primaryContact: false,
    },
    emergencyContact: isOnline ? null : {
      contactName: '', contactNo: '', relationship: '',
    },
    student: {
      givenName: '', middlename: '', surname: '', email: '', contactNo: '', gender: '',
      address: '',
      suburb: '',
      postcode: '',
      schoolName: '', schoolGrade: '', dateOfBirth: null,
    },
    heardAboutUs: [],
    preferredCourses: [{ courseName: '', year: '', term: '' }],
    medicalAndOtherInfo: {
      studentHealthInfoOrAllergies: '',
      agreeToCosts: isOnline ? null : false,
      agreeToPaymentTerms: false,
      admin_confirms_consent: false,
    },
    tenant_id: tenantId ?? 0,
    enrolmentMode: enrolmentMode,
};

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

          const cardbg = useColorModeValue(
            (mode === 'review' || mode === 'create-by-admin') ? "gray.50" : "blue.50",
            (mode === 'review' || mode === 'create-by-admin') ? "gray.800" : "blue.800"
          );

          const borderColor = useColorModeValue(
            (mode === 'review' || mode === 'create-by-admin') ? 'gray.300' : 'blue.300',
            (mode === 'review' || mode === 'create-by-admin') ? 'gray.600' : 'blue.600'
          );

          const borderRadius = (mode === 'review' || mode === 'create-by-admin') ? "sm" : "lg";
          const size = (mode === 'review' || mode === 'create-by-admin') ? "sm" : "md";
          const borderWidth = (mode === 'review' || mode === 'create-by-admin') ? 1 : 3;

          const submitButtonCaption = mode === 'review' ? "Update Enrolment" : "Submit"

            const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

            useEffect(() => {
              if (formReset) {
                setEnrolmentData(initialEnrolmentData);
                if (recaptchaRef.current) {
                  recaptchaRef.current.reset();
                }
              }
            }, [formReset]);

            // NEW — react to enrolmentMode changing after mount (e.g. an admin toggle on the
            // create-by-admin screen). Without this, fields seeded under the previous mode
            // (father/emergencyContact populated with empty strings under offline, or left
            // null under online) silently persist in state even after the visible sections
            // change, producing a payload that doesn't match what's on screen.
            useEffect(() => {
              setEnrolmentData((prevData) => ({
                ...prevData,
                father: isOnline ? null : (prevData.father ?? {
                  givenName: '', surname: '', email: '', contactNo: '', primaryContact: false,
                }),
                emergencyContact: isOnline ? null : (prevData.emergencyContact ?? {
                  contactName: '', contactNo: '', relationship: '',
                }),
                student: {
                  ...prevData.student,
                  // address/suburb/postcode stay required in both modes now — leave untouched
                },
                medicalAndOtherInfo: {
                  ...prevData.medicalAndOtherInfo,
                  agreeToCosts: isOnline ? null : (prevData.medicalAndOtherInfo.agreeToCosts ?? false),
                },
                enrolmentMode: enrolmentMode,
              }));
              // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [enrolmentMode]);

            const [enrolmentData, setEnrolmentData] = useState<EnrolmentData>(
              mode === 'review' && studentEnrolmentData
                  ? {
                      ...studentEnrolmentData,
                      student: {
                          ...studentEnrolmentData.student,
                          dateOfBirth: studentEnrolmentData.student.dateOfBirth
                              ? new Date(studentEnrolmentData.student.dateOfBirth)
                              : null,
                      }
                  }
                  : initialEnrolmentData
            );

            const handleRecaptchaChange = (token: string | null) => {
              setRecaptchaToken(token);
            };

              const currentYear = new Date().getFullYear();
              const nextYear = currentYear + 1;

              const addCourse = () => {
                setEnrolmentData((prevData) => ({
                  ...prevData,
                  preferredCourses: [...prevData.preferredCourses, { courseName: '', year: '', term: '' }],
                }));
              };

              const removeCourse = (index: number) => {
                if (index === 0) return;
                setEnrolmentData((prevData) => ({
                  ...prevData,
                  preferredCourses: prevData.preferredCourses.filter((_, idx) => idx !== index),
                }));
              };

              const handleCourseChange = (index: number, field: keyof PreferredCourse, value: string) => {
                const updatedCourses = [...enrolmentData.preferredCourses];
                updatedCourses[index][field] = value;
                setEnrolmentData((prevData) => ({
                  ...prevData,
                  preferredCourses: updatedCourses,
                }));
              };

              const handleChange = (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
              ) => {
                const { name, value, type } = e.target;
                const isCheckbox = type === 'checkbox';
                const newValue = isCheckbox ? (e.target as HTMLInputElement).checked : value;

                if (name === 'heardAboutUs') {
                  const selectedValue = (e.target as HTMLInputElement).value;

                  setEnrolmentData((prevData) => {
                    const updatedHeardAboutUs = newValue
                      ? [...prevData.heardAboutUs, selectedValue]
                      : prevData.heardAboutUs.filter((item) => item !== selectedValue);

                    return {
                      ...prevData,
                      heardAboutUs: updatedHeardAboutUs,
                    };
                  });
                  return;
                }

                const [parentKey, fieldKey] = name.split('.');

                setEnrolmentData((prevData) => {
                  if (parentKey === 'tenant_id') {
                    return {
                      ...prevData,
                      tenant_id: Number(newValue),
                    };
                  }

                  if (parentKey && fieldKey) {
                    if (fieldKey === 'primaryContact' && isCheckbox) {
                      return {
                        ...prevData,
                        mother: {
                          ...prevData.mother,
                          primaryContact: parentKey === 'mother',
                        },
                        father: prevData.father
                          ? { ...prevData.father, primaryContact: parentKey === 'father' }
                          : prevData.father,
                      };
                    }

                    const parentData = prevData[parentKey as keyof EnrolmentData];

                    if (parentData === null && (parentKey === 'father' || parentKey === 'emergencyContact')) {
                      return {
                        ...prevData,
                        [parentKey]: {
                          [fieldKey]: newValue,
                        },
                      };
                    }

                    if (typeof parentData === 'object' && parentData !== null) {
                      return {
                        ...prevData,
                        [parentKey]: {
                          ...parentData,
                          [fieldKey]: newValue,
                        },
                      };
                    }
                  }

                  return prevData;
                });
              };

              const handleDateChange = (
                e: React.ChangeEvent<HTMLInputElement>
              ) => {
                const { name, value } = e.target;
                const dateValue = value ? new Date(value) : null;

                const [parentKey, fieldKey] = name.split('.');

                setEnrolmentData((prevData) => {
                  if (parentKey === 'tenant_id') {
                    console.error("Cannot set a date for tenant_id");
                    return prevData;
                  }

                  const parentData = prevData[parentKey as keyof EnrolmentData];
                  if (typeof parentData === 'object' && parentData !== null) {
                    return {
                      ...prevData,
                      [parentKey]: {
                        ...parentData,
                        [fieldKey]: dateValue,
                      },
                    };
                  }

                  return prevData;
                });
              };

            const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();

              if (mode == "public"){
                if (!recaptchaToken) {
                  alert('Please complete the reCAPTCHA verification.');
                  return;
                }

                const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";

                const response = await fetch(apiurl+"/verify-captcha", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ recaptchaToken }),
                });

                const data = await response.json();

                if (data.success) {
                 } else {
                  alert("CAPTCHA verification failed.");
                   return;
                 }

              console.log('Form data and reCAPTCHA token:', enrolmentData, recaptchaToken);

              }

              if (!isOnline && !enrolmentData.medicalAndOtherInfo.agreeToCosts) {
                    alert('Please agree to the medical/cost terms before submitting the enrolment.');
                    return;
                  }
                  if (!enrolmentData.medicalAndOtherInfo.agreeToPaymentTerms) {
                    alert('Please agree to the payment terms before submitting the enrolment.');
                    return;
                  }

              if (enrolmentData.heardAboutUs.length === 0) {
                alert('Please select at least one option on how you have heard about us');
                return;
              }
              console.log(enrolmentData);
              onSave(enrolmentData);

            }


            const recaptchaKey = process.env.REACT_APP_GOOGLE_CAPTCHA_KEY;

            if (!recaptchaKey) {
              throw new Error('REACT_APP_GOOGLE_CAPTCHA_KEY is not defined');
            }



  return (
    <Box>
      <Box as="form" onSubmit={handleSubmit} >

        <Box mt={2}  borderColor={borderColor} borderWidth={borderWidth} borderRadius={borderRadius} >
        {/* CHANGED: heading text now reflects that online only ever collects one guardian */}
        <Heading mt={2} ml={2} size={size} >{isOnline ? "Parent/Guardian Details" : "Parent's Details"}</Heading>
            <Box mt ={6} ml={8} mr={8} mb={4} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
                {/* CHANGED: label no longer implies "mother specifically" when online */}
                <Text mb={4} fontSize={size} fontWeight="bold">{isOnline ? "Primary Guardian" : "Mother/Female Guardian"}</Text>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <Input
                      name="mother.givenName"
                      placeholder="Given Name*"
                      value={enrolmentData.mother.givenName}
                      onChange={handleChange}
                      size={size}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="mother.surname"
                      placeholder="Surname*"
                      value={enrolmentData.mother.surname}
                      onChange={handleChange}
                      size={size}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="mother.email"
                      type="email"
                      placeholder="Email*"
                      value={enrolmentData.mother.email}
                      onChange={handleChange}
                      size={size}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="mother.contactNo"
                      placeholder="Contact No.*"
                      value={enrolmentData.mother.contactNo}
                      onChange={handleChange}
                      size={size}
                    />
                  </FormControl>
                  {/* CHANGED: primaryContact checkbox hidden for online — meaningless with a single guardian.
                      State stays true (set in initialEnrolmentData) without needing user interaction. */}
                  {!isOnline && (
                    <FormControl>
                      <Checkbox
                        name="mother.primaryContact"
                        isChecked={enrolmentData.mother.primaryContact}
                        onChange={handleChange}
                        size={size}
                      >
                        Primary Contact
                      </Checkbox>
                    </FormControl>
                  )}
                </VStack>
              </Box>

      {!isOnline && (
        <Box mt={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
          <Text mb={4} fontSize={size} fontWeight="bold">Father/Male Guardian</Text>
          <VStack spacing={4}>
            <FormControl isRequired>
              <Input name="father.givenName" placeholder="Given Name*" value={enrolmentData.father?.givenName ?? ''} onChange={handleChange} size={size} />
            </FormControl>
            <FormControl isRequired>
              <Input name="father.surname" placeholder="Surname*" value={enrolmentData.father?.surname ?? ''} onChange={handleChange} size={size} />
            </FormControl>
            <FormControl isRequired>
              <Input name="father.email" type="email" placeholder="Email*" value={enrolmentData.father?.email ?? ''} onChange={handleChange} size={size} />
            </FormControl>
            <FormControl isRequired>
              <Input name="father.contactNo" placeholder="Contact No.*" value={enrolmentData.father?.contactNo ?? ''} onChange={handleChange} size={size} />
            </FormControl>
            <FormControl>
              <Checkbox name="father.primaryContact" isChecked={enrolmentData.father?.primaryContact ?? false} onChange={handleChange} size={size}>
                Primary Contact
              </Checkbox>
            </FormControl>
          </VStack>
        </Box>
      )}

{!isOnline && (
            <Box mt={6} ml={8} mr={8} mb={4} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
              <Text mb={4} fontSize={size} fontWeight="bold">Emergency Contact</Text>

              <VStack spacing={4}>
                <FormControl isRequired>
                  <Input
                    name="emergencyContact.relationship"
                    placeholder="Relationship to Student*"
                    value={enrolmentData.emergencyContact?.relationship ?? ''}
                    size={size}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <Input
                    name="emergencyContact.contactName"
                    placeholder="Full Name of Contact*"
                    value={enrolmentData.emergencyContact?.contactName ?? ''}
                    size={size}
                    onChange={handleChange}
                  />
                </FormControl>

                <FormControl isRequired>
                  <Input
                    name="emergencyContact.contactNo"
                    placeholder="Contact No.*"
                    value={enrolmentData.emergencyContact?.contactNo ?? ''}
                    size={size}
                    onChange={handleChange}
                  />
                </FormControl>
              </VStack>
            </Box>
          )}
        </Box>


        <Box mt={4} borderColor={borderColor} borderWidth={borderWidth} borderRadius={borderRadius}  >
        <Heading mt={2} ml={2} size={size}>Student's Details</Heading>
          <Box mt ={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
            <Text mb={4} fontSize={size} fontWeight="bold">Identity Details</Text>
            <VStack spacing={4}>
              <FormControl isRequired>
                <Input
                  name="student.givenName"
                  placeholder="Given Name*"
                  value={enrolmentData.student.givenName}
                  size={size}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl >
                <Input
                  name="student.middlename"
                  placeholder="Middlename"
                  value={enrolmentData.student.middlename}
                  size={size}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl isRequired>
                <Input
                  name="student.surname"
                  placeholder="Surname*"
                  value={enrolmentData.student.surname}
                  size={size}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl isRequired>
                <Select
                  name="student.gender"
                  placeholder="Select Gender*"
                  value={enrolmentData.student.gender}
                  size={size}
                  onChange={handleChange}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </Select>
              </FormControl>
              <FormControl isRequired>
                <HStack>
              <FormLabel fontSize={size} ml={4} minWidth={110}>Date of Birth</FormLabel>
                <Input
                  name="student.dateOfBirth"
                  type="date"
                  size={size}
                  value={enrolmentData.student.dateOfBirth ? enrolmentData.student.dateOfBirth.toISOString().split('T')[0] : ''}
                  onChange={handleDateChange}
                />
                </HStack>
              </FormControl>
              </VStack>
          </Box>

          <Box mt={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">

              <Text mb={4} fontSize={size} fontWeight="bold">Address & Contact Details</Text>
              <VStack>
             
                <>
                  <FormControl isRequired>
                    <Input
                      name="student.address"
                      placeholder="Address*"
                      size={size}
                      value={enrolmentData.student.address ?? ''}
                      onChange={handleChange}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="student.suburb"
                      placeholder="Suburb*"
                      size={size}
                      value={enrolmentData.student.suburb ?? ''}
                      onChange={handleChange}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="student.postcode"
                      placeholder="Postcode*"
                      size={size}
                      value={enrolmentData.student.postcode ?? ''}
                      onChange={handleChange}
                    />
                  </FormControl>
                </>
              
              <FormControl isRequired>
                <Input
                  name="student.email"
                  type="email"
                  placeholder="Student's Email - In case student doesnt have his or her email id. Please share parent's email id."
                  size={size}
                  value={enrolmentData.student.email}
                  onChange={handleChange}
                />
              </FormControl>
              <FormControl >
                <Input
                  name="student.contactNo"
                  placeholder="Contact No."
                  size={size}
                  value={enrolmentData.student.contactNo}
                  onChange={handleChange}
                />
              </FormControl>
              </VStack>
            </Box>

            <Box mt={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">

              <Text mb={4} fontSize={size} fontWeight="bold">Education Details</Text>
              <VStack>
                  <FormControl isRequired>
                    <Input
                      name="student.schoolName"
                      placeholder="Student's School Name*"
                      size={size}
                      value={enrolmentData.student.schoolName}
                      onChange={handleChange}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <Input
                      name="student.schoolGrade"
                      placeholder="Student's School Grade*"
                      size={size}
                      value={enrolmentData.student.schoolGrade}
                      onChange={handleChange}
                    />
                  </FormControl>
              </VStack>
            </Box>


            <Box mt={6} ml={8} mr={8} mb ={4} bgColor={cardbg} borderRadius={borderRadius}p={4} boxShadow="lg">
              <Text mb={4} fontSize={size} fontWeight="bold">Preferred Course(s)</Text>
              <VStack spacing={4} align="stretch">
                {enrolmentData.preferredCourses.map((course, index) => (
                  <HStack key={index} spacing={3}>
                    <FormControl>
                      <Input
                        placeholder="Course Name"
                        value={course.courseName}
                        size={size}
                        onChange={(e) => handleCourseChange(index, 'courseName', e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <Select
                        placeholder="Year"
                        value={course.year}
                        size={size}
                        onChange={(e) => handleCourseChange(index, 'year', e.target.value)}
                      >
                         <option value={currentYear}>{currentYear}</option>
                         <option value={nextYear}>{nextYear}</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        placeholder="Term"
                        value={course.term}
                        size={size}
                        onChange={(e) => handleCourseChange(index, 'term', e.target.value)}
                      >
                        <option value="Term 1">Term 1</option>
                        <option value="Term 2">Term 2</option>
                        <option value="Term 3">Term 3</option>
                        <option value="Term 4">Term 4</option>
                      </Select>
                    </FormControl>
                    <Button size={size} colorScheme="blue" onClick={addCourse}>
                      +
                    </Button>
                    {index > 0 && (
                      <Button size={size} colorScheme="red" onClick={() => removeCourse(index)}>
                        -
                      </Button>
                    )}
                  </HStack>
                ))}
              </VStack>
            </Box>

        </Box>


        <Box mt={4} borderColor={borderColor} borderWidth={borderWidth} borderRadius={borderRadius}  >
        {/* CHANGED: heading reflects that the medical box beneath it is gone for online */}
        <Heading mt={2} ml={2} size={size}>{isOnline ? "Other Details" : "Medical & Other Details"}</Heading>

        {/* CHANGED: entire health/allergies box hidden for online — no in-person duty-of-care trigger exists */}
        {!isOnline && (
          <Box mt ={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
              <Text mb={4} fontSize={size} fontWeight="bold">Student Emergency Health Information</Text>
             <VStack>
                  <FormControl>
                    <FormLabel fontSize={size}>Any allergies and/or medical conditions:</FormLabel>
                    <Textarea
                      name="medicalAndOtherInfo.studentHealthInfoOrAllergies"
                      value={enrolmentData.medicalAndOtherInfo.studentHealthInfoOrAllergies}
                      onChange={handleChange}
                      size={size}
                    />
                  </FormControl>
                </VStack>
          </Box>
        )}

        <Box mt={6} ml={8} mr={8} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
              <Text mb={4} fontSize={size} fontWeight="bold">How did you hear about us?*</Text>
          <FormControl>
                  <VStack align="flex-start">
                    {['Family or Friends', 'Social Media', 'Internet', 'Outdoor Signage', 'Google', 'Others'].map((option) => (
                      <Checkbox
                        key={option}
                        name="heardAboutUs"
                        value={option}
                        size={size}
                        isChecked={enrolmentData.heardAboutUs.includes(option)}
                        onChange={handleChange}
                      >
                        {option}
                      </Checkbox>
                    ))}
                  </VStack>
                </FormControl>
            </Box>

      <Box mt={6} ml={8} mr={8} mb={4} bgColor={cardbg} borderRadius={borderRadius} p={4} boxShadow="lg">
        <Text mb={4} fontSize={size} fontWeight="bold">Enrolment Terms</Text>
        <VStack spacing={6}>
          {!isOnline && (
            <FormControl>
              <Checkbox
                name="medicalAndOtherInfo.agreeToCosts"
                size={size}
                isChecked={enrolmentData.medicalAndOtherInfo.agreeToCosts ?? false}
                onChange={(e) => handleChange(e)}
              >
                {mode === 'create-by-admin'
                  ? `I confirm the parent/guardian has agreed that in the event a doctor/ambulance is called by ${tenantName}, they will be responsible for all costs associated with the patient's treatment and transport.`
                  : `In the event that a doctor/ambulance is to be called by ${tenantName}, I agree that I will be responsible for all costs associated with the patient's treatment and transport.`}
              </Checkbox>
            </FormControl>
          )}

          <FormControl>
            <Checkbox
              name="medicalAndOtherInfo.agreeToPaymentTerms"
              size={size}
              isChecked={enrolmentData.medicalAndOtherInfo.agreeToPaymentTerms}
              onChange={(e) => handleChange(e)}
            >
              {mode === 'create-by-admin'
                ? "I confirm the parent/guardian has agreed with the payment terms and conditions."
                : "In the event that I proceed with my child's enrolment I agree with the payment terms and conditions."}
            </Checkbox>
          </FormControl>

          {mode === 'create-by-admin' && (
            <FormControl isRequired>
              <Checkbox
                name="medicalAndOtherInfo.admin_confirms_consent"
                size={size}
                isChecked={enrolmentData.medicalAndOtherInfo.admin_confirms_consent}
                onChange={(e) => handleChange(e)}
              >
                I confirm I have obtained and securely stored the parent/guardian's written consent (email, scanned signed form, etc.). I understand the tenant is responsible for maintaining proof of consent.
              </Checkbox>
            </FormControl>
          )}
        </VStack>
      </Box>

        </Box>

        <Box mt={4}>

            {mode === "public" && (
            <ReCAPTCHA sitekey={recaptchaKey} onChange={handleRecaptchaChange} ref={recaptchaRef}  />
          )}
            </Box>
                <Button size={size} mt={4} colorScheme="blue" type="submit">
                {submitButtonCaption}
                </Button>
            </Box>
        </Box>

  );
};

export default EnrolmentForm;